<template>
  <div>
    <div style="margin-bottom: 20px;">
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        :clearable="false"
        format="yyyy. MM. dd."
        value-format="yyyy-MM-dd"
        :picker-options="dateRangeOptions"
        style="margin-right: 10px;"
        @change="fetchData"
      />
      <el-button
        style="float: right;"
        :loading="downloadLoading"
        :disabled="!dateRange"
        icon="el-icon-download"
        @click="download"
      >
        Letöltés
      </el-button>
    </div>
    <el-table
      v-if="dateRange"
      v-loading="loading"
      :data="priceBreakdown"
    >
      <el-table-column
        label="#"
        prop="creatorUserId"
        width="70"
      >
      </el-table-column>
      <el-table-column
        label="Képviselő"
        prop="creatorUserName"
      >
      </el-table-column>
      <el-table-column
        v-for="priceType in priceTypes"
        :key="priceType.id"
        :label="priceType.name"
        :prop="`pricetype_${priceType.id}`"
        align="right"
      >
        <template slot-scope="scope">
          {{ scope.row[`pricetype_${priceType.id}`] | currency }}
        </template>
      </el-table-column>
      <el-table-column
        label="Egyedi ár"
        prop="pricetype_null"
        align="right"
      >
        <template slot-scope="scope">
          {{ scope.row.pricetype_null | currency }}
        </template>
      </el-table-column>
      <el-table-column
        label="Összesen"
        prop="pricetype_sum"
        align="right"
      >
        <template slot-scope="scope">
          {{ scope.row.pricetype_sum | currency }}
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import moment from 'moment';
import { saveAs } from 'file-saver';

export default {
  data() {
    const start = moment()
      .startOf('year')
      .format('Y-MM-DD');
    const end = moment()
      .format('Y-MM-DD');
    return {
      loading: false,
      downloadLoading: false,
      userLoading: false,
      dateRange: [start, end],
      representativeUserId: null,
      representativeUsers: [],
      priceTypes: [],
      dateRangeOptions: {
        firstDayOfWeek: 1,
        shortcuts: [{
          text: 'Aktuális hét',
          onClick(picker) {
            const start = moment()
              .weekday(1)
              .toDate();
            const end = moment()
              .weekday(7)
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző hét',
          onClick(picker) {
            const start = moment()
              .subtract({ weeks: 1 })
              .weekday(1)
              .toDate();
            const end = moment()
              .subtract({ weeks: 1 })
              .weekday(7)
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Aktuális hónap',
          onClick(picker) {
            const start = moment()
              .startOf('month')
              .toDate();
            const end = moment()
              .endOf('month')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző hónap',
          onClick(picker) {
            const start = moment()
              .subtract({ months: 1 })
              .startOf('month')
              .toDate();
            const end = moment()
              .subtract({ months: 1 })
              .endOf('month')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző 3 hónap',
          onClick(picker) {
            const start = moment()
              .subtract({ months: 3 })
              .startOf('month')
              .toDate();
            const end = moment()
              .subtract({ months: 1 })
              .endOf('month')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Aktuális év',
          onClick(picker) {
            const start = moment()
              .startOf('year')
              .toDate();
            const end = moment()
              .endOf('year')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'Előző év',
          onClick(picker) {
            const start = moment()
              .subtract({ years: 1 })
              .startOf('year')
              .toDate();
            const end = moment()
              .subtract({ years: 1 })
              .endOf('year')
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'YTD',
          onClick(picker) {
            const start = moment()
              .startOf('year')
              .toDate();
            const end = moment()
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }, {
          text: 'MTD',
          onClick(picker) {
            const start = moment()
              .startOf('month')
              .toDate();
            const end = moment()
              .toDate();
            picker.$emit('pick', [start, end]);
          }
        }]
      },
      priceBreakdown: [],
    };
  },
  methods: {
    async download() {
      this.downloadLoading = true;
      try {
        const excel = await axios({
          url: `api/reports/price-breakdown/download`,
          method: 'POST',
          responseType: 'blob',
          data: {
            from: this.dateRange[0],
            to: this.dateRange[1],
          },
        });
        saveAs(excel, `pricebreakdown.xlsx`);
      } catch (e) {}
      this.downloadLoading = false;
    },
    async fetchData() {
      this.loading = true;
      try {
        this.priceTypes = await axios.get('api/price-types');
        const purePriceBreakdown = await axios.get(`api/reports/price-breakdown/${this.dateRange.join(',')}`);
        const transformed = [];
        purePriceBreakdown.forEach(user => {
          let sum = 0;
          const priceTypes = {};
          this.priceTypes.forEach(priceType => {
            if (priceType.is_active) {
              const selectedPriceType = user.price_types.find(pt => pt.price_type_id === priceType.id);
              sum += selectedPriceType && selectedPriceType.net || 0;
              priceTypes[`pricetype_${priceType.id}`] = selectedPriceType && selectedPriceType.net || 0;
            }
          });
          const selectedPriceType = user.price_types.find(pt => pt.price_type_id === null);
          sum += selectedPriceType && selectedPriceType.net || 0;
          priceTypes[`pricetype_null`] = selectedPriceType && selectedPriceType.net || 0;

          transformed.push({
            creatorUserId: user.creator_user_id,
            creatorUserName: user.creator_user_name,
            ...priceTypes,
            pricetype_sum: sum,
          });
        });
        this.priceBreakdown = transformed;
      } catch (e) {
      }
      this.loading = false;
    }
  },
  async mounted() {
    await this.fetchData();
  },
};
</script>
